import { cloneDeep } from 'lodash'
import { getProductListData, _getPRDataById } from '../products/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import type { IProductKitItem, TProduct } from '~/types/product/models'
import { assertIsDefined, assertIsString } from '~/types/models'

let isInitializingKitItemData = false
const productKitItemListData = undefined as unknown as IProductKitItem[]

export const getProductKitItemListData = getListDataFunctionFactory<
  IProductKitItem[]
>({
  listData: productKitItemListData,
  storageKey: 'productkititems',
  getStartingListData: getStartingProductKitItemListData,
})

export async function getProductKitItemsData(
  parentProductId: string
): Promise<IProductKitItem[]> {
  const fn = 'getProductKitItemsData |'
  console.debug(
    `${fn} Getting kit data for parentProductId '${parentProductId}'`
  )
  const productKitItemListData = await getProductKitItemListData()

  console.debug(
    `${fn} productKitItemListData before filtering for ${parentProductId}:`,
    productKitItemListData
  )

  const listData = productKitItemListData.filter(
    (item: IProductKitItem) => item.parentProduct?.id === parentProductId
  )
  console.debug(
    `${fn} parentProductId: ${parentProductId}, listData:`,
    listData
  )
  return listData ?? []
}

export async function incrementParentKitNumKitItems({
  updatedEntityData: entityData,
}: {
  updatedEntityData: IProductKitItem
}): Promise<void> {
  await incrementDecrementParentKitNumKitItems({
    entityData,
    action: 'increment',
  })
}

// increment/decrement below for numKitItems is necessary
// in mock because several select lists use numKitItems > 0
// as a criterion for displaying a kit product in a
// select list.

export async function decrementParentKitNumKitItems({
  deletedEntityData: entityData,
}: {
  deletedEntityData: IProductKitItem
}): Promise<void> {
  await incrementDecrementParentKitNumKitItems({
    entityData,
    action: 'decrement',
  })
}

export async function incrementDecrementParentKitNumKitItems({
  entityData,
  action,
}: {
  entityData: IProductKitItem
  action: 'increment' | 'decrement'
}): Promise<void> {
  const fn = 'incrementDecrementParentKitNumKitItems |'
  console.debug(`${fn} entity data`, entityData)
  const parentProductId = entityData.parentProduct?.id
  assertIsString(parentProductId)
  const stListData = await getProductListData()
  const parentProduct = await _getPRDataById(parentProductId, stListData, false)
  assertIsDefined(parentProduct, fn, 'parentProduct')
  console.debug(`${fn}`, cloneDeep(parentProduct))
  console.debug(
    `${fn} ${action}ing numKitItems. Before: ${parentProduct.numKitItems}`
  )
  if (
    parentProduct.numKitItems === undefined ||
    parentProduct.numKitItems === null
  ) {
    parentProduct.numKitItems = 0
  }
  if (action === 'increment') {
    parentProduct.numKitItems += 1
  } else {
    parentProduct.numKitItems = Math.max(parentProduct.numKitItems - 1, 0)
  }
  console.debug(`${fn}`, cloneDeep(parentProduct))
  console.debug(
    `${fn} ${action}ing numKitItems. After: ${parentProduct.numKitItems}`
  )
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'products',
    listData: stListData,
    callingFn: fn,
  })
}

export async function getStartingProductKitItemListData() {
  isInitializingKitItemData = true
  const stList = await getProductListData()

  const pr0003 = await _getPRDataById(
    'XXPR0003',
    stList,
    isInitializingKitItemData
  ) // License (SDWAN)
  const pr0005 = await _getPRDataById(
    'XXPR0005',
    stList,
    isInitializingKitItemData
  )
  const pr0007 = await _getPRDataById(
    'XXPR0007',
    stList,
    isInitializingKitItemData
  )
  const pr0009 = await _getPRDataById(
    'XXPR0009',
    stList,
    isInitializingKitItemData
  )

  const pr0016 = await _getPRDataById(
    'XXPR0016',
    stList,
    isInitializingKitItemData
  )
  const pr0017 = await _getPRDataById(
    'XXPR0017',
    stList,
    isInitializingKitItemData
  )
  const pr0018 = await _getPRDataById(
    'XXPR0018',
    stList,
    isInitializingKitItemData
  )
  const pr0019 = await _getPRDataById(
    'XXPR0019',
    stList,
    isInitializingKitItemData
  ) // SDWAN (Kit)
  const pr0020 = await _getPRDataById(
    'XXPR0020',
    stList,
    isInitializingKitItemData
  ) // V10Box
  const pr0021 = await _getPRDataById(
    'XXPR0021',
    stList,
    isInitializingKitItemData
  ) // V120Box
  const pr0022 = await _getPRDataById(
    'XXPR0022',
    stList,
    isInitializingKitItemData
  ) // V180Box
  const pr0023 = await _getPRDataById(
    'XXPR0023',
    stList,
    isInitializingKitItemData
  ) // V1800Box
  const pr0026 = await _getPRDataById(
    'XXPR0026',
    stList,
    isInitializingKitItemData
  ) // standard license
  const pr0027 = await _getPRDataById(
    'XXPR0027',
    stList,
    isInitializingKitItemData
  ) // e911 Service Fee
  const pr0028 = await _getPRDataById(
    'XXPR0028',
    stList,
    isInitializingKitItemData
  ) // Compliance and Administrative Cost Reocvery Fee
  const pr0029 = await _getPRDataById(
    'XXPR0029',
    stList,
    isInitializingKitItemData
  ) // TAM - Silver
  const pr0030 = await _getPRDataById(
    'XXPR0030',
    stList,
    isInitializingKitItemData
  ) // TAM - Gold
  const pr0031 = await _getPRDataById(
    'XXPR0031',
    stList,
    isInitializingKitItemData
  ) // TAM - Bronze
  const pr0032 = await _getPRDataById(
    'XXPR0032',
    stList,
    isInitializingKitItemData
  ) // TAM (Kit)
  const pr0064 = await _getPRDataById(
    'XXPR0064',
    stList,
    isInitializingKitItemData
  ) // SDWAN Device (Set)

  const productKitItemListData: IProductKitItem[] = [
    {
      id: 'XXPRKI0009',
      hrid: 'PRKI0009',
      parentProduct: {
        id: 'XXPR0013',
        label: 'E-LAN (Kit)',
      },
      product: pr0007!,
      // Managed Router is a free-form add-on on this plain kit.
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0010',
      hrid: 'PRKI0010',
      parentProduct: {
        id: 'XXPR0013',
        label: 'E-LAN (Kit)',
      },
      product: pr0009!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0011',
      hrid: 'PRKI0011',
      parentProduct: {
        id: 'XXPR0013',
        label: 'E-LAN (Kit)',
      },
      product: pr0016!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0012',
      hrid: 'PRKI0012',
      parentProduct: {
        id: 'XXPR0013',
        label: 'E-LAN (Kit)',
      },
      product: pr0016!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0013',
      hrid: 'PRKI0013',
      parentProduct: {
        id: 'XXPR0016',
        label: 'E-LAN Hub (Kit)',
      },
      product: pr0018!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0014',
      hrid: 'PRKI0014',
      parentProduct: {
        id: 'XXPR0016',
        label: 'E-LAN Hub (Kit)',
      },
      product: pr0005!,
      // Edge Router is a free-form add-on on this plain kit.
      isOptional: true,
      quantity: 'x2',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0015',
      hrid: 'PRKI0015',
      parentProduct: {
        id: 'XXPR0016',
        label: 'E-LAN Hub (Kit)',
      },
      product: pr0017!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0016',
      hrid: 'PRKI0016',
      parentProduct: {
        id: 'XXPR0017',
        label: 'E-LAN Endpoint (Kit)',
      },
      product: pr0018!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0017',
      hrid: 'PRKI0017',
      parentProduct: {
        id: 'XXPR0017',
        label: 'E-LAN Endpoint (Kit)',
      },
      product: pr0005!,
      // Edge Router is a free-form add-on on this plain kit.
      isOptional: true,
      quantity: 'x2',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0018',
      hrid: 'PRKI0018',
      parentProduct: {
        id: 'XXPR0019',
        label: 'SDWAN (Kit)',
      },
      product: pr0009!,
      // The kit's free-form add-on: independent of the SDWAN Device Set
      // (XXPRKI0059), and taken or not on its own.
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0019',
      hrid: 'PRKI0019',
      parentProduct: {
        id: 'XXPR0019',
        label: 'SDWAN (Kit)',
      },
      product: pr0003!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0020',
      hrid: 'PRKI0020',
      parentProduct: {
        id: 'XXPR0064',
        label: 'SDWAN Device (Set)',
      },
      product: pr0020!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0021',
      hrid: 'PRKI0021',
      parentProduct: {
        id: 'XXPR0064',
        label: 'SDWAN Device (Set)',
      },
      product: pr0021!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0022',
      hrid: 'PRKI0022',
      parentProduct: {
        id: 'XXPR0064',
        label: 'SDWAN Device (Set)',
      },
      product: pr0022!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0023',
      hrid: 'PRKI0023',
      parentProduct: {
        id: 'XXPR0064',
        label: 'SDWAN Device (Set)',
      },
      product: pr0023!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    // ---- NEW ----
    {
      id: 'XXPRKI0024',
      hrid: 'PRKI0024',
      parentProduct: {
        id: 'XXPR0025',
        label: 'UCaaS (Kit)',
      },
      product: pr0026!, // standard license
      quantity: 'kit',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXPRKI0025',
      hrid: 'PRKI0025',
      parentProduct: {
        id: 'XXPR0025',
        label: 'UCaaS Kit',
      },
      product: pr0028!, // Compliance and Administrative Cost Reocvery Fee
      quantity: 'kit',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXPRKI0026',
      hrid: 'PRKI0026',
      parentProduct: {
        id: 'XXPR0025',
        label: 'UCaaS Kit',
      },
      product: pr0027!, // e911 Service Fee
      quantity: 'kit',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXPRKI0027',
      hrid: 'PRKI0027',
      parentProduct: {
        id: 'XXPR0032',
        label: 'Technical Account Manager (Set)',
      },
      product: pr0031!, // TAM - Bronze
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0028',
      hrid: 'PRKI0028',
      parentProduct: {
        id: 'XXPR0032',
        label: 'Technical Account Manager (Set)',
      },
      product: pr0029!, // TAM - Silver
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0029',
      hrid: 'PRKI0029',
      parentProduct: {
        id: 'XXPR0032',
        label: 'Technical Account Manager (Set)',
      },
      product: pr0030!, // TAM - Gold
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0030',
      hrid: 'PRKI0030',
      parentProduct: {
        id: 'XXPR0025',
        label: 'UCaaS Kit',
      },
      product: pr0032!, // TAM (Kit)
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0031',
      hrid: 'PRKI0031',
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0036',
        stList,
        isInitializingKitItemData
      ))!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0032',
      hrid: 'PRKI0032',
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0050',
        stList,
        isInitializingKitItemData
      ))!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0033',
      hrid: 'PRKI0033',
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0054',
        stList,
        isInitializingKitItemData
      ))!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0034',
      hrid: 'PRKI0034',
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0060',
        stList,
        isInitializingKitItemData
      ))!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0035',
      hrid: 'PRKI0035',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0037',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0036',
      hrid: 'PRKI0036',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0038',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0037',
      hrid: 'PRKI0037',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0039',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0038',
      hrid: 'PRKI0038',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0040',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0039',
      hrid: 'PRKI0039',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0042',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0040',
      hrid: 'PRKI0040',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0043',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0041',
      hrid: 'PRKI0041',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0044',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0042',
      hrid: 'PRKI0042',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0045',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0043',
      hrid: 'PRKI0043',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0046',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0044',
      hrid: 'PRKI0044',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0047',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0045',
      hrid: 'PRKI0045',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0048',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0046',
      hrid: 'PRKI0046',
      parentProduct: {
        id: 'XXPR0036',
        label: 'DIA Bandwidth (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0049',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0047',
      hrid: 'PRKI0047',
      parentProduct: {
        id: 'XXPR0050',
        label: 'DIA Access (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0051',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0048',
      hrid: 'PRKI0048',
      parentProduct: {
        id: 'XXPR0050',
        label: 'DIA Access (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0052',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0049',
      hrid: 'PRKI0049',
      parentProduct: {
        id: 'XXPR0050',
        label: 'DIA Access (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0053',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0050',
      hrid: 'PRKI0050',
      parentProduct: {
        id: 'XXPR0054',
        label: 'IP Address (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0055',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0051',
      hrid: 'PRKI0051',
      parentProduct: {
        id: 'XXPR0054',
        label: 'IP Address (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0056',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0052',
      hrid: 'PRKI0052',
      parentProduct: {
        id: 'XXPR0054',
        label: 'IP Address (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0057',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0053',
      hrid: 'PRKI0053',
      parentProduct: {
        id: 'XXPR0054',
        label: 'IP Address (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0058',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0054',
      hrid: 'PRKI0054',
      parentProduct: {
        id: 'XXPR0054',
        label: 'IP Address (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0059',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0055',
      hrid: 'PRKI0055',
      parentProduct: {
        id: 'XXPR0060',
        label: 'CPE (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0007',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0056',
      hrid: 'PRKI0056',
      parentProduct: {
        id: 'XXPR0060',
        label: 'CPE (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0061',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0057',
      hrid: 'PRKI0057',
      parentProduct: {
        id: 'XXPR0060',
        label: 'CPE (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0062',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0058',
      hrid: 'PRKI0058',
      parentProduct: {
        id: 'XXPR0060',
        label: 'CPE (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0063',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      // How a kit holds a Set -- the same shape as XXPRKI0031, DIA (Kit) ->
      // DIA Bandwidth (Set). Not optional itself: the kit always has a device
      // slot, and the Set decides WHICH device fills it.
      id: 'XXPRKI0059',
      hrid: 'PRKI0059',
      parentProduct: {
        id: 'XXPR0019',
        label: 'SDWAN (Kit)',
      },
      product: pr0064!,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      // A SET INSIDE A SET (see XXPR0065): Managed Router (Set) as one of CPE
      // (Set)'s alternatives. Optional, like its siblings XXPRKI0055-0058 --
      // it IS an alternative, and the parity guard needs the catalogue to call
      // it optional wherever a supplier does.
      id: 'XXPRKI0060',
      hrid: 'PRKI0060',
      parentProduct: {
        id: 'XXPR0060',
        label: 'CPE (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0065',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0061',
      hrid: 'PRKI0061',
      parentProduct: {
        id: 'XXPR0065',
        label: 'Managed Router (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0066',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0062',
      hrid: 'PRKI0062',
      parentProduct: {
        id: 'XXPR0065',
        label: 'Managed Router (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0067',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    // Branch Office -- the optional nested plain Kit. See XXPR0068.
    {
      id: 'XXPRKI0063',
      hrid: 'PRKI0063',
      parentProduct: {
        id: 'XXPR0068',
        label: 'Branch Office (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0066',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      // An optional PLAIN Kit: an add-on, header and contents together.
      id: 'XXPRKI0064',
      hrid: 'PRKI0064',
      parentProduct: {
        id: 'XXPR0068',
        label: 'Branch Office (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0069',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0065',
      hrid: 'PRKI0065',
      parentProduct: {
        id: 'XXPR0069',
        label: 'Wi-Fi (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0071',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'x2',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0066',
      hrid: 'PRKI0066',
      parentProduct: {
        id: 'XXPR0069',
        label: 'Wi-Fi (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0072',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      // A mandatory Kit INSIDE the option: it follows the option.
      id: 'XXPRKI0067',
      hrid: 'PRKI0067',
      parentProduct: {
        id: 'XXPR0069',
        label: 'Wi-Fi (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0070',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0068',
      hrid: 'PRKI0068',
      parentProduct: {
        id: 'XXPR0070',
        label: 'Wi-Fi Mounting (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0073',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    // Zscaler DLP - 3 Year -- three year-kits in one Kit. See XXPR0074.
    {
      id: 'XXPRKI0069',
      hrid: 'PRKI0069',
      parentProduct: {
        id: 'XXPR0074',
        label: 'Zscaler DLP - 3 Year (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0075',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0070',
      hrid: 'PRKI0070',
      parentProduct: {
        id: 'XXPR0074',
        label: 'Zscaler DLP - 3 Year (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0075',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0071',
      hrid: 'PRKI0071',
      parentProduct: {
        id: 'XXPR0074',
        label: 'Zscaler DLP - 3 Year (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0075',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0072',
      hrid: 'PRKI0072',
      parentProduct: {
        id: 'XXPR0075',
        label: 'Data Loss Protection (DLP) (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0076',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0073',
      hrid: 'PRKI0073',
      parentProduct: {
        id: 'XXPR0075',
        label: 'Data Loss Protection (DLP) (Kit)',
      },
      product: (await _getPRDataById(
        'XXPR0077',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: false,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0074',
      hrid: 'PRKI0074',
      parentProduct: {
        id: 'XXPR0076',
        label: 'DLP Licensing (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0078',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPRKI0075',
      hrid: 'PRKI0075',
      parentProduct: {
        id: 'XXPR0077',
        label: 'SASE Support & Implementation (Set)',
      },
      product: (await _getPRDataById(
        'XXPR0078',
        stList,
        isInitializingKitItemData
      ))!,
      isOptional: true,
      quantity: 'kit',
      version: '1',
      canBeDeleted: false,
    },
  ]
  isInitializingKitItemData = false
  return productKitItemListData
}
